import React, {ChangeEvent, PropsWithChildren, useCallback} from 'react';
import b_ from 'b_';
import {useFieldArray, Controller, useFormContext} from 'react-hook-form';
import get from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';

import I18N from 'assets/i18n/brandlift-project';

import {CreativeBox} from 'client/common/types';

import {Button, Icon, Select, Textinput} from 'client/common/components/lego';
import Block from 'client/common/components/block';

import {OptionLists, setImmut, wrapArray} from './utils';
import {ChoiceType, CustomQuestionFormValue} from './types';

const b = b_.with('creative-settings-form');
const i18n = I18N.creatives;

const CHOICE_OPTIONS = [
    {
        value: 'single',
        content: I18N.surveyStats.columnChartInfo.choice
    },
    {
        value: 'multi',
        content: I18N.surveyStats.columnChartInfo.multiChoice
    }
];

function useChangeCustomQuestions(box: CreativeBox, updateSurveyBoxContext: (box: unknown) => Promise<void>) {
    return useCallback(async (questionFormValues: CustomQuestionFormValue[]) => {
        await updateSurveyBoxContext(setImmut(box, 'context.customQuestions.questions', questionFormValues.map(question => {
            return {
                label: question.label,
                multipleChoice: question.type === 'multi',
                options: question.options.map(({text}) => text)
            };
        })));
    }, [box, updateSurveyBoxContext]);
}

type CustomQuestionProps = Pick<CustomQuestionsProps, 'box' | 'disabled' | 'updateSurveyBoxContext'> & PropsWithChildren<{
    index: number;
    isRemovable?: boolean;
    questionField: {label?: string; type?: ChoiceType; id?: string};
}>;

function CustomQuestion(props: CustomQuestionProps) {
    const {
        box,
        questionField,
        disabled,
        index: questionIndex,
        updateSurveyBoxContext,
        children
    } = props;

    // eslint-disable-next-line @typescript-eslint/unbound-method
    const {register, control, errors, getValues} = useFormContext();

    const changeCustomQuestion = useChangeCustomQuestions(box, updateSurveyBoxContext);

    const {fields: optionsFields, append, remove: removeOption} = useFieldArray({
        control,
        name: `customQuestions[${questionIndex}].options`
    });

    const handleBlur = useCallback(async () => {
        const questions = getValues().customQuestions as CustomQuestionFormValue[];

        await changeCustomQuestion(questions);
    }, [changeCustomQuestion, getValues]);

    const handleOptionAdd = useCallback(async () => {
        const option = {text: ''};

        append(option, false);

        const questions = getValues().customQuestions;
        const options = cloneDeep(optionsFields);

        options.push(option);

        questions[questionIndex].options = options;

        await changeCustomQuestion(questions);
    }, [append, changeCustomQuestion, getValues, optionsFields, questionIndex]);

    const handleOptionRemove = useCallback(async (index: number) => {
        removeOption(index);

        const questions = getValues().customQuestions;
        const options = cloneDeep(optionsFields);

        options.splice(index, 1);

        questions[questionIndex].options = options;

        await changeCustomQuestion(questions);
    }, [changeCustomQuestion, getValues, optionsFields, questionIndex, removeOption]);

    return (
        <div className={b('question-group', {custom: true})}>
            <div className={b('custom-question')}>
                <div className={b('option-list-row')}>
                    <Textinput
                        size='m'
                        view='borderless'
                        className={b('row')}
                        name={`customQuestions[${questionIndex}].label`}
                        placeholder={i18n.creativeSettings.questionPlaceholder}
                        controlRef={register({
                            required: i18n.creativeSettings.validationMsg.customQuestionRequired
                        })}
                        onBlur={handleBlur}
                        state={get(errors, `customQuestions.${questionIndex}.label`) && 'error'}
                        defaultValue={questionField.label}
                        disabled={disabled}
                    />
                </div>
                {errors.customQuestions?.[questionIndex]?.label && (
                    <span className={b('errors')}>
                        {errors.customQuestions[questionIndex].label.message}
                    </span>
                )}
            </div>
            <OptionLists
                register={register}
                errors={errors}
                rules={{required: i18n.creativeSettings.validationMsg.customAnswer}}
                fields={optionsFields}
                fieldName={`customQuestions[${questionIndex}].options`}
                disabled={disabled}
                onBlur={() => handleBlur}
                onAppend={handleOptionAdd}
                onRemove={handleOptionRemove}
                minItems={2}
                maxItems={4}
                addItemBtnText={i18n.creativeSettings.addBtnText.custom}
                hasNoOpinion
            >
                <Controller
                    control={control}
                    name={`customQuestions[${questionIndex}].type`}
                    defaultValue={questionField.type}
                    render={render => {
                        return (
                            <Select
                                className={b('choice-select')}
                                onChange={async e => {
                                    render.onChange(e.target.value);
                                    await handleBlur();
                                }}
                                options={CHOICE_OPTIONS}
                                value={render.value}
                                size='m'
                                iconProps={{
                                    type: 'arrow'
                                }}
                                view='dl-light'
                                disabled={disabled}
                            />
                        );
                    }}
                />
            </OptionLists>
            {children}
        </div>
    );
}

interface CustomQuestionsProps {
    box: CreativeBox;
    maxQuestions: number;
    disabled: boolean;
    isCustomQuestionsEnabled: boolean;
    handleCustomQuestionUpdate: (e: ChangeEvent<HTMLInputElement>) => Promise<void>;
    updateSurveyBoxContext: (box: unknown) => Promise<void>;
}

export default function CustomQuestions(props: CustomQuestionsProps) {
    const {
        box,
        maxQuestions,
        disabled,
        isCustomQuestionsEnabled,
        handleCustomQuestionUpdate,
        updateSurveyBoxContext
    } = props;

    const changeCustomQuestion = useChangeCustomQuestions(box, updateSurveyBoxContext);

    // eslint-disable-next-line @typescript-eslint/unbound-method
    const {control, setValue, getValues} = useFormContext();

    const {fields, remove} = useFieldArray({
        control,
        name: 'customQuestions'
    });

    const addQuestion = async () => {
        const question: CustomQuestionFormValue = {
            label: '',
            type: 'single',
            options: wrapArray(['', ''])
        };

        const questions = [...getValues().customQuestions, question];

        setValue('customQuestions', questions);

        await changeCustomQuestion(questions);
    };

    const removeQuestion = async (questionIndex: number) => {
        remove(questionIndex);

        const questions = getValues().customQuestions;

        questions.splice(questionIndex, 1);

        await changeCustomQuestion(questions);
    };

    const fieldsLength = fields?.filter(Boolean).length ?? 0;

    return (
        <Block
            header={i18n.creativeSettings.extraQuestionsTitle}
            description={i18n.creativeSettings.extraQuestionsDesc}
            tumbler={{
                checked: isCustomQuestionsEnabled,
                disabled,
                onChange: handleCustomQuestionUpdate
            }}
        >
            {isCustomQuestionsEnabled && fields?.map((item: any, index: number) => {
                return (
                    <CustomQuestion
                        key={item.id}
                        box={box}
                        index={index}
                        questionField={item}
                        disabled={disabled}
                        updateSurveyBoxContext={updateSurveyBoxContext}
                    >
                        {!disabled && fieldsLength > 1 && (
                            <Button
                                className={b('option-list-remove-question-btn')}
                                size='m'
                                view='default'
                                onClick={async () => removeQuestion(index)}
                                title={i18n.creativeSettings.removeBtnTitle.default}
                                iconLeft={(cls: string) => <Icon className={cls} type='trash' />}
                            />
                        )}
                    </CustomQuestion>
                );
            })}
            {isCustomQuestionsEnabled && !disabled && fieldsLength < maxQuestions && (
                <Button
                    className={b('option-list-add-question-btn')}
                    view='dl-default'
                    size='m'
                    width='max'
                    onClick={addQuestion}
                >
                    {I18N.surveyEditor.page.addQuestion}
                </Button>
            )}
        </Block>
    );
}
